// src/promotion/index.js — the promotion matrix, phase 1: the read-only
// dossier (v129.28). Bundled to dist/promotion.js (globalName "Promotion"),
// loaded by admin.html.
//
// Requested by the area managers via Matt, 3 Aug 2026.
// Design of record: docs/promotion-matrix-design.md. Read it before changing
// anything here — several of the choices below look like omissions and are not.
//
// THE TWO THAT MATTER MOST:
//   1. No score. There is no overall, no readiness percentage, no traffic
//      light on the person. Every figure is shown as what it is, with its
//      period. If you add a composite number, the page starts making the
//      decision instead of informing it.
//   2. The caveats are not decoration. They come from the API, generated from
//      the actual gaps in THIS person's data, and they render ABOVE the
//      figures — because a fairness note nobody reaches is a fairness note
//      nobody read. Attendance looks worse for people whose rota was entered
//      wrong, and we have proved that happens (v129.22).
//
// The overlay builds its own DOM, so admin.html needs only the <script> tag.
// Bare-name globals (DB, currentAdmin, showToast) are typeof-guarded — the
// classic-script bundle contract.

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function toast(msg, kind) {
  if (typeof showToast === 'function') showToast(msg, kind);
}
function api(path, body) {
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, {
    method: body ? 'POST' : 'GET',
    headers: headers,
    body: body ? JSON.stringify(body) : undefined,
    cache: 'no-store',
  });
}

// v129.30 (phase 2) — the write side. Every one of these re-reads the dossier
// afterwards rather than patching local state: the server owns the open-objective
// cap and the "a status needs an objective" rule, so the page must show what the
// server actually did, not what it hoped.
async function post(path, body, okMsg) {
  try {
    var res = await api(path, body);
    var out = await res.json().catch(function () { return {}; });
    if (!res.ok) { toast(out.error || ('HTTP ' + res.status), 'error'); return false; }
    if (okMsg) toast(okMsg);
    await load();
    return true;
  } catch (e) {
    toast((e && e.message) || String(e), 'error');
    return false;
  }
}

async function addObjective() {
  var t = document.getElementById('promoObjText');
  var d = document.getElementById('promoObjDate');
  var l = document.getElementById('promoObjLadder');
  if (!t) return;
  var text = String(t.value || '').trim();
  if (!text) { toast('Write the objective first.', 'error'); return; }
  var ok = await post('/api/admin/promotion/objective', {
    staff_id: _staffId, text: text,
    target_date: (d && d.value) || null,
    ladder_key: (l && l.value) || null,
  }, 'Objective set. They will see it in My Development.');
  if (ok && t) t.value = '';
}

async function setObjectiveState(id, state) {
  var d = _data || {};
  var o = (d.objectives || []).filter(function (x) { return String(x.id) === String(id); })[0];
  if (!o) return;
  await post('/api/admin/promotion/objective',
    { staff_id: _staffId, id: id, text: o.text, ladder_key: o.ladder_key, target_date: o.target_date, state: state },
    state === 'met' ? 'Marked as met.' : 'Dropped.');
}

async function setStatus(status) {
  var n = document.getElementById('promoNote');
  var l = document.getElementById('promoStatusLadder');
  await post('/api/admin/promotion/status', {
    staff_id: _staffId,
    status: status || null,
    ladder_key: (l && l.value) || null,
    private_note: n ? n.value : undefined,
  }, status ? 'Status set — they can see it too.' : 'Status cleared.');
}

async function saveNote() {
  var d = _data || {};
  if (!d.status || !d.status.status) { toast('Set a status first — the note belongs to it.', 'error'); return; }
  var n = document.getElementById('promoNote');
  var l = document.getElementById('promoStatusLadder');
  await post('/api/admin/promotion/status', {
    staff_id: _staffId, status: d.status.status,
    ladder_key: (l && l.value) || d.status.ladder_key || null,
    private_note: n ? n.value : '',
  }, 'Note saved.');
}

// Who gets the button at all. Mirrors the server gate rather than trusting it
// to be the only check — a button that 403s is a worse experience than no
// button, and the server stays the authority either way.
function canSee() {
  if (typeof currentAdmin === 'undefined' || !currentAdmin) return false;
  var r = String(currentAdmin.role || '').toLowerCase();
  return r === 'admin' || r === 'area_manager';
}

var _months = 12;
var _staffId = null;
var _data = null;
// v129.31 (phase 3) — the JACK conversation. In memory only, wiped when the
// overlay closes: the design says the transcript is not stored, and a manager
// thinking out loud about somebody is not a record anybody should keep.
var _chat = [];
var _chatBusy = false;
var _drafts = [];

function overlay() {
  var el = document.getElementById('promoOverlay');
  if (el) return el;
  el = document.createElement('div');
  el.id = 'promoOverlay';
  el.style.cssText = 'display:none;position:fixed;inset:0;z-index:9000;background:var(--overlay);' +
    'padding:16px;overflow-y:auto;-webkit-overflow-scrolling:touch';
  el.addEventListener('click', function (e) { if (e.target === el) close(); });
  document.body.appendChild(el);
  return el;
}

function open(staffId) {
  if (!canSee()) { toast('The promotion matrix is for Head Office and area managers.', 'error'); return; }
  _staffId = staffId;
  _months = 12;
  _data = null;
  _chat = [];
  _drafts = [];
  var el = overlay();
  el.style.display = '';
  paint('<div style="padding:40px;text-align:center;color:var(--sub)">Gathering the record…</div>');
  load();
}

function close() {
  var el = document.getElementById('promoOverlay');
  if (el) el.style.display = 'none';
  _data = null;
  _staffId = null;
  _chat = [];          // the transcript dies with the overlay, deliberately
  _drafts = [];
  _chatBusy = false;
}

function setMonths(m) {
  var n = parseInt(m, 10);
  if (!n || isNaN(n)) return;
  _months = n;
  paint('<div style="padding:40px;text-align:center;color:var(--sub)">Re-reading the last ' + n + ' months…</div>');
  load();
}

async function load() {
  try {
    var res = await api('/api/admin/promotion-dossier?staff_id=' + encodeURIComponent(_staffId) + '&months=' + _months);
    var body = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      paint('<div style="padding:32px"><div style="font-weight:600;margin-bottom:6px">Can\'t show this</div>' +
        '<div style="color:var(--sub)">' + esc(body.error || ('HTTP ' + res.status)) + '</div>' +
        '<div style="margin-top:16px"><button class="btn btn-outline" onclick="Promotion.close()">Close</button></div></div>');
      return;
    }
    _data = body;
    paint(render(body));
  } catch (e) {
    paint('<div style="padding:32px"><div style="font-weight:600">Couldn\'t reach the server</div>' +
      '<div style="color:var(--sub);margin-top:6px">' + esc(String(e && e.message || e)) + '</div>' +
      '<div style="margin-top:16px"><button class="btn btn-outline" onclick="Promotion.close()">Close</button></div></div>');
  }
}

function paint(inner) {
  var el = overlay();
  el.innerHTML = '<div style="max-width:860px;margin:0 auto;background:var(--card);border-radius:14px;' +
    'box-shadow:0 20px 50px rgba(0,0,0,.25);overflow:hidden">' + inner + '</div>';
}

// ── rendering ───────────────────────────────────────────────────────────────
function n(v, dash) { return (v == null || v === '') ? (dash || '—') : String(v); }
function pct(v) { return v == null ? '—' : (v + '%'); }

function statCard(label, value, sub) {
  return '<div style="flex:1 1 130px;min-width:130px;background:var(--surface-2);border-radius:10px;padding:12px 14px">' +
    '<div style="font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--sub)">' + esc(label) + '</div>' +
    '<div style="font-size:22px;font-weight:600;margin-top:2px;font-variant-numeric:tabular-nums">' + esc(String(value)) + '</div>' +
    (sub ? '<div style="font-size:11px;color:var(--sub);margin-top:2px">' + esc(sub) + '</div>' : '') +
    '</div>';
}
function section(title, note, inner) {
  return '<div style="padding:18px 20px;border-top:1px solid var(--border)">' +
    '<div style="font-weight:600;margin-bottom:10px">' + esc(title) + '</div>' +
    inner +
    (note ? '<div style="font-size:12px;color:var(--sub);margin-top:10px;line-height:1.5">' + esc(note) + '</div>' : '') +
    '</div>';
}
function row(cells) {
  return '<div style="display:flex;gap:10px;flex-wrap:wrap">' + cells.join('') + '</div>';
}


// ── phase 2: the assessment panel (v129.30) ────────────────────────────────
// Sits BETWEEN the ladder and the facts, deliberately: what a manager writes
// is the point of the page, and the figures are what they weigh it against.
//
// Everything in here except the note is visible to the person (decision 1).
// The note box says so on itself rather than relying on the writer remembering.
var STATUS_LABEL = { one_to_watch: 'One to Watch', ready: 'Ready' };

function objectiveRow(o) {
  var done = o.state === 'met', dropped = o.state === 'dropped';
  // Dubai is UTC+4. A bare toISOString() flags an objective due TODAY as
  // overdue for the last four hours of every working day.
  var dubaiNow = new Date(Date.now() + 4 * 3600 * 1000);
  var overdue = o.state === 'open' && o.target_date && o.target_date < dubaiNow.toISOString().slice(0, 10);
  return '<div style="display:flex;gap:10px;align-items:flex-start;padding:9px 0;border-top:1px solid var(--border)">' +
    '<div style="flex:1">' +
      '<div translate="no" style="font-size:13.5px;line-height:1.5;' + (done || dropped ? 'color:var(--sub);text-decoration:line-through' : '') + '">' +
        esc(o.text) + '</div>' +
      '<div style="font-size:11.5px;color:var(--sub);margin-top:3px">' +
        (o.target_date ? (overdue ? '<b style="color:var(--ui-warn)">by ' + esc(o.target_date) + ' — overdue</b>' : 'by ' + esc(o.target_date)) : 'no target date') +
        (o.set_by_name ? ' · set by <span translate="no">' + esc(o.set_by_name) + '</span>' : '') +
        (done ? ' · <b>met</b>' : dropped ? ' · dropped' : '') +
        (o.is_new ? ' · <span style="color:var(--info-deep)">not seen yet</span>' : '') +
      '</div>' +
    '</div>' +
    (o.state === 'open'
      ? '<div style="display:flex;gap:6px;flex-shrink:0">' +
          '<button class="btn btn-outline btn-sm" onclick="Promotion.setObjectiveState(' + Number(o.id) + ",'met')\">Met</button>" +
          '<button class="btn btn-outline btn-sm" onclick="Promotion.setObjectiveState(' + Number(o.id) + ",'dropped')\">Drop</button>" +
        '</div>'
      : '') +
  '</div>';
}

function assessmentHtml(d) {
  var st = d.status || null;
  var objectives = d.objectives || [];
  var openCount = objectives.filter(function (o) { return o.state === 'open'; }).length;
  var ladders = d.ladders || [];

  var ladderSelect = function (id, selected) {
    if (!ladders.length) return '';
    return '<select class="form-select" id="' + id + '" style="width:auto;font-size:12.5px">' +
      '<option value="">(no step named)</option>' +
      ladders.map(function (l) {
        return '<option value="' + esc(l.key) + '"' + (selected === l.key ? ' selected' : '') + '>to ' + esc(l.to) + '</option>';
      }).join('') + '</select>';
  };

  return '<div style="margin:0 20px 16px;border:1px solid var(--border);border-radius:12px;overflow:hidden">' +
    '<div style="padding:12px 14px;background:var(--surface-2);display:flex;gap:10px;align-items:center;flex-wrap:wrap">' +
      '<div style="font-weight:600;font-size:13.5px">Development</div>' +
      (st && st.status
        ? '<span style="background:var(--info-bg);color:var(--info-deep);border-radius:999px;padding:2px 10px;font-size:12px;font-weight:600">' +
          esc(STATUS_LABEL[st.status] || st.status) + '</span>' +
          '<span style="font-size:11.5px;color:var(--sub)">set by <span translate="no">' + esc(st.set_by_name || 'someone') + '</span></span>'
        : '<span style="font-size:12px;color:var(--sub)">No status</span>') +
      '<div style="flex:1"></div>' +
      ladderSelect('promoStatusLadder', st && st.ladder_key) +
      '<button class="btn btn-outline btn-sm" onclick="Promotion.setStatus(\'one_to_watch\')">One to Watch</button>' +
      '<button class="btn btn-outline btn-sm" onclick="Promotion.setStatus(\'ready\')">Ready</button>' +
      (st && st.status ? '<button class="btn btn-outline btn-sm" onclick="Promotion.setStatus(null)">Clear</button>' : '') +
    '</div>' +

    '<div style="padding:12px 14px">' +
      '<div style="font-size:12px;color:var(--sub);margin-bottom:6px">' +
        'Objectives — <b>' + openCount + ' open</b>. ' +
        'They will see these, and their status, in My Development.' +
      '</div>' +
      (objectives.length ? objectives.map(objectiveRow).join('') :
        '<div style="font-size:13px;color:var(--sub);padding:6px 0">Nothing set yet.</div>') +

      '<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;align-items:flex-start">' +
        '<textarea id="promoObjText" rows="2" placeholder="What does ready look like? Say what done means, not what is missing." ' +
          'style="flex:1;min-width:240px;border:1.5px solid var(--border);border-radius:10px;padding:9px 12px;' +
          'font-size:13px;font-family:inherit;resize:vertical"></textarea>' +
        '<input type="date" id="promoObjDate" style="border:1.5px solid var(--border);border-radius:10px;padding:9px 10px;font-size:13px;font-family:inherit">' +
        ladderSelect('promoObjLadder', null) +
        '<button class="btn btn-green btn-sm" onclick="Promotion.addObjective()" style="padding:9px 16px">Set objective</button>' +
      '</div>' +

      // The private note. Labelled as private ON the box — a manager writing
      // into an unlabelled field next to fields the subject reads will assume
      // the wrong thing exactly once, and that once is the damaging one.
      '<div style="margin-top:14px">' +
        '<div style="font-size:12px;color:var(--sub);margin-bottom:4px">' +
          'Your assessment — <b>not shown to them</b>. Objectives and status are; this is your working note.' +
        '</div>' +
        '<textarea id="promoNote" rows="3" placeholder="Your reasoning. Kept with the record." ' +
          'style="width:100%;box-sizing:border-box;border:1.5px solid var(--border);border-radius:10px;padding:9px 12px;' +
          'font-size:13px;font-family:inherit;resize:vertical">' + esc((st && st.private_note) || '') + '</textarea>' +
        '<div style="margin-top:6px"><button class="btn btn-outline btn-sm" onclick="Promotion.saveNote()">Save note</button></div>' +
      '</div>' +
    '</div>' +
  '</div>';
}


// ── phase 3: talking it through with JACK (v129.31) ─────────────────────────
// The AM says what they think; JACK helps turn it into objectives. He drafts,
// they save — every draft below lands in the objective box for editing, and
// the saved row is attributed to the AM. JACK's name never goes on it.
async function ask() {
  var input = document.getElementById('promoAsk');
  if (!input || _chatBusy) return;
  var text = String(input.value || '').trim();
  if (!text) return;
  input.value = '';
  _chat.push({ role: 'user', content: text });
  _drafts = [];
  _chatBusy = true;
  paint(render(_data));
  try {
    var res = await api('/api/admin/promotion/advise', {
      staff_id: _staffId,
      months: _months,
      ladder_key: (document.getElementById('promoStatusLadder') || {}).value || null,
      messages: _chat,
    });
    var out = await res.json().catch(function () { return {}; });
    if (!res.ok || !out.ok) {
      _chat.push({ role: 'assistant', content: 'I could not answer that — ' +
        (out.error || out.reason || ('HTTP ' + res.status)) + '.' });
    } else {
      _chat.push({ role: 'assistant', content: out.text || '(no reply)' });
      _drafts = out.drafts || [];
    }
  } catch (e) {
    _chat.push({ role: 'assistant', content: 'I could not reach the server — ' + ((e && e.message) || e) + '.' });
  }
  _chatBusy = false;
  paint(render(_data));
  var box = document.getElementById('promoChatLog');
  if (box) box.scrollTop = box.scrollHeight;
}

// A draft goes into the objective BOX, not into the database. The AM reads it,
// changes what they want, and presses Set — which is the whole of decision 3.
function useDraft(i) {
  var t = _drafts[i];
  if (!t) return;
  var box = document.getElementById('promoObjText');
  if (!box) return;
  box.value = t;
  box.focus();
  toast('Dropped into the objective box — edit it, then press Set objective.');
}

function chatHtml() {
  var log = _chat.map(function (m) {
    var mine = m.role === 'user';
    return '<div style="display:flex;justify-content:' + (mine ? 'flex-end' : 'flex-start') + ';margin:6px 0">' +
      '<div translate="no" style="max-width:82%;padding:8px 11px;border-radius:12px;font-size:13px;line-height:1.55;white-space:pre-wrap;' +
        (mine ? 'background:var(--info-bg);color:var(--text)' : 'background:var(--surface-2);color:var(--text)') + '">' +
        esc(m.content) + '</div></div>';
  }).join('');

  var drafts = _drafts.length
    ? '<div style="margin-top:8px;padding-top:8px;border-top:1px solid var(--border)">' +
      '<div style="font-size:11.5px;color:var(--sub);margin-bottom:5px">Drafts — edit before you set them. They save under your name, not his.</div>' +
      _drafts.map(function (t, i) {
        return '<div style="display:flex;gap:8px;align-items:flex-start;margin:5px 0">' +
          '<div translate="no" style="flex:1;font-size:13px;line-height:1.5">' + esc(t) + '</div>' +
          '<button class="btn btn-outline btn-sm" onclick="Promotion.useDraft(' + i + ')">Use</button>' +
        '</div>';
      }).join('') + '</div>'
    : '';

  return '<div style="margin:0 20px 16px;border:1px solid var(--border);border-radius:12px;overflow:hidden">' +
    '<div style="padding:10px 14px;background:var(--surface-2);display:flex;gap:8px;align-items:center;flex-wrap:wrap">' +
      '<div style="font-weight:600;font-size:13.5px">🤖 Talk it through with JACK</div>' +
      '<div style="font-size:11.5px;color:var(--sub)">He drafts objectives. He cannot see or change anything else, and he never saves.</div>' +
    '</div>' +
    '<div style="padding:12px 14px">' +
      (_chat.length
        ? '<div id="promoChatLog" style="max-height:280px;overflow-y:auto">' + log +
          (_chatBusy ? '<div style="font-size:12.5px;color:var(--sub);padding:6px 2px">Thinking…</div>' : '') + '</div>'
        : '<div style="font-size:12.5px;color:var(--sub);line-height:1.55">' +
          'Say what you actually think — <i>"I do not think they are ready to run a store yet"</i> — and he will work back ' +
          'to what "ready" would look like. He will not tell you whether to promote anybody; that is your call.' +
          '</div>') +
      drafts +
      '<div style="display:flex;gap:8px;margin-top:10px">' +
        '<input id="promoAsk" placeholder="What do you think about them?" ' +
          'onkeydown="if(event.key===\'Enter\'){event.preventDefault();Promotion.ask();}" ' +
          'style="flex:1;border:1.5px solid var(--border);border-radius:10px;padding:9px 12px;font-size:13px;font-family:inherit"' +
          (_chatBusy ? ' disabled' : '') + '>' +
        '<button class="btn btn-green btn-sm" onclick="Promotion.ask()" style="padding:9px 16px"' +
          (_chatBusy ? ' disabled' : '') + '>Ask</button>' +
      '</div>' +
    '</div>' +
  '</div>';
}

function render(d) {
  var s = d.staff || {};
  var sec = d.sections || {};
  var h = '';

  h += '<div style="padding:18px 20px;display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap">' +
    '<div style="flex:1 1 240px">' +
      '<div translate="no" style="font-size:19px;font-weight:600">' + esc(s.name || 'Unknown') + '</div>' +
      '<div translate="no" style="color:var(--sub);font-size:13px;margin-top:2px">' + esc(n(s.role)) + ' · ' + esc(n(s.store)) + '</div>' +
    '</div>' +
    '<div style="display:flex;gap:8px;align-items:center">' +
      '<select class="form-select" style="width:auto" onchange="Promotion.setMonths(this.value)">' +
        [3, 6, 12, 24].map(function (m) {
          return '<option value="' + m + '"' + (m === d.months ? ' selected' : '') + '>Last ' + m + ' months</option>';
        }).join('') +
      '</select>' +
      '<button class="btn btn-outline" onclick="Promotion.close()">Close</button>' +
    '</div>' +
  '</div>';

  // The step being considered. No recommendation attached — the ladder is
  // context for the reader, not a suggestion from the system.
  var ladders = d.ladders || [];
  h += '<div style="padding:0 20px 16px">' +
    (ladders.length
      ? '<div style="font-size:13px;color:var(--sub)">Steps from here: ' +
          ladders.map(function (l) { return '<b>' + esc(l.to) + '</b>'; }).join(' · ') + '</div>'
      : '<div style="font-size:13px;color:var(--sub)">There is no promotion step above this role in Shift, so this page is a record, not a matrix.</div>') +
  '</div>';

  h += assessmentHtml(d);
  h += chatHtml();

  // Caveats FIRST. See the header comment — this ordering is the point, not a
  // layout accident, and there is a test pinning it.
  if (d.caveats && d.caveats.length) {
    h += '<div style="margin:0 20px 4px;padding:12px 14px;background:var(--ap-orange-bg);border:1px solid var(--ap-orange-line);border-radius:10px">' +
      '<div style="font-weight:600;font-size:13px;color:var(--ap-orange-deep);margin-bottom:6px">Read these before the numbers</div>' +
      '<ul style="margin:0;padding-inline-start:18px;font-size:12.5px;line-height:1.6;color:var(--ap-orange-deep)">' +
      d.caveats.map(function (c) { return '<li>' + esc(c) + '</li>'; }).join('') +
      '</ul></div>';
  }

  var t = sec.tenure || {};
  h += section('Time here and training', t.note, row([
    statCard('With us since', t.since ? t.since : 'Not recorded', t.months != null ? t.months + ' months' : ''),
    statCard('Modules passed', t.modulesPassed == null ? '—' : (t.modulesPassed + '/6'), t.fullyInducted ? 'Fully inducted' : ''),
  ]));

  var a = sec.attendance || {};
  h += section('Punctuality and attendance',
    'On time is measured against the rostered start with ' + n(a.graceMin, '5') + ' minutes\' grace — the same rule as the Right On Time badge.',
    row([
      statCard('On time', pct(a.onTimePct), a.comparable != null ? 'of ' + a.comparable + ' comparable days' : ''),
      statCard('Late', n(a.late, '0'), a.worstLateMin ? 'worst ' + a.worstLateMin + ' min' : ''),
      statCard('Rostered days', n(a.scheduled, '0'), ''),
      statCard('Not clocked', n(a.missed, '0'), 'rostered but no clock-in'),
    ]));

  var lv = sec.leave || {};
  var ld = lv.days || {};
  h += section('Leave and TOIL', lv.note, row([
    statCard('Holiday', n(ld.holiday, '0'), 'days'),
    statCard('Sick', n(ld.sick, '0'), 'days'),
    statCard('TOIL taken', n(ld.toil, '0'), 'days'),
    statCard('Approved leave', n(ld.apl, '0'), 'days'),
  ]) + (lv.balances
    ? '<div style="font-size:12px;color:var(--sub);margin-top:8px">Current balances (' + esc(String(lv.balances.year)) + '): holiday ' +
      esc(n(lv.balances.holiday)) + ' · TOIL ' + esc(n(lv.balances.toil)) + '</div>'
    : ''));

  var r = sec.reviews || {};
  h += section('Reviews',
    r.draftsHidden > 0 ? r.draftsHidden + ' unfinished draft(s) are not shown — a draft is a working document, not a fact about somebody.' : null,
    (r.count === 0
      ? '<div style="color:var(--sub);font-size:13px">No finalised review.</div>'
      : '<div style="font-size:13px;line-height:1.7">' +
          '<div>Latest: <b>' + esc(n(r.latest && r.latest.quarter)) + '</b> — ' +
            esc(r.latest && r.latest.overall != null ? r.latest.overall + '/5' : 'no score') +
            (r.latest && r.latest.reviewer ? ' (<span translate="no">' + esc(r.latest.reviewer) + '</span>)' : '') + '</div>' +
          (r.history && r.history.length > 1
            ? '<div style="color:var(--sub);margin-top:4px">History: ' +
              r.history.slice().reverse().map(function (x) { return esc(x.quarter) + ' ' + esc(String(x.overall)); }).join(' → ') +
              '</div>'
            : '') +
        '</div>'));

  var rec = sec.recognition || {};
  h += section('Recognition',
    'The full record — unlike the clock-in tab, which shows stars and hearts for 7 days.',
    row([
      statCard('⭐ Stars', n(rec.stars, '0'), ''),
      statCard('💙 Hearts', n(rec.hearts, '0'), ''),
      statCard('Badges', n(rec.total, '0'), 'all time'),
    ]) + ((rec.badges && rec.badges.length)
      ? '<div style="font-size:12px;color:var(--sub);margin-top:8px">' +
        rec.badges.map(function (b) { return esc(b.badge_key) + ' ×' + b.count; }).join(' · ') + '</div>'
      : ''));

  var c = sec.contribution || {};
  h += section('Add-on contribution', c.note, row([
    statCard('Add-ons logged', n(c.total, '0'), c.daysLogged ? 'on ' + c.daysLogged + ' days' : ''),
  ]) + ((c.byCategory && c.byCategory.length)
    ? '<div style="font-size:12px;color:var(--sub);margin-top:8px">' +
      c.byCategory.map(function (x) { return esc(x.category) + ' ' + x.count; }).join(' · ') + '</div>'
    : ''));

  var cm = sec.compliance || {};
  h += section('Store compliance — ' + n(cm.store), cm.note,
    row([
      statCard('Logs missed', cm.logsMissedTotal == null ? '—' : cm.logsMissedTotal, 'across ' + n(cm.days, '0') + ' days'),
      statCard('Checklists done', n(cm.checklists && cm.checklists.completed, '0'),
        (cm.checklists && cm.checklists.expected) ? 'of ' + cm.checklists.expected + ' expected' : ''),
    ]));

  h += '<div style="padding:14px 20px;border-top:1px solid var(--border);background:var(--surface-2);' +
    'font-size:12px;color:var(--sub);line-height:1.6">' +
    'Facts only — Shift does not rate anybody or recommend a promotion. The judgement, and the words it is written in, are yours. ' +
    'Objectives, the development status and JACK\'s help arrive in the next phase.' +
    '</div>';

  return h;
}

// The button that opens it, for the staff Directory rows. Returns '' when the
// viewer isn't entitled — so the markup simply doesn't exist for a store
// manager rather than existing and failing.
function buttonHtml(staffId) {
  if (!canSee()) return '';
  return '<button class="btn btn-outline btn-sm" title="Promotion matrix" ' +
    'onclick="Promotion.open(' + Number(staffId) + ')">📈</button>';
}

window.Promotion = {
  open: open,
  close: close,
  setMonths: setMonths,
  buttonHtml: buttonHtml,
  canSee: canSee,
  addObjective: addObjective,
  setObjectiveState: setObjectiveState,
  setStatus: setStatus,
  saveNote: saveNote,
  ask: ask,
  useDraft: useDraft,
};

export {
  open, close, setMonths, buttonHtml, canSee,
  addObjective, setObjectiveState, setStatus, saveNote, ask, useDraft,
};
